import type { IPreset } from "../interfaces/IPreset";
import { isPresetId } from "../presetId";
import { presetDataFromInstance } from "../variableOptionInstanceKeys";

/** Which of a preset's names an export writes as `name`. */
export type PresetExportTexts = "german" | "english";

/** A preset as the list atom carries it. */
export type ExportablePreset = IPreset & { id: string };

/** A preset as `@emerge/lt-cases` reads it from `presets.json`. */
export interface ExportedPreset {
  id: string;
  name: string;
  productType: string;
  category: string;
  tags?: readonly string[];
  data: Record<string, unknown>;
}

/**
 * The part of a user option the preset export reads: an `IListUserOption`
 * entry of the user options list, with its entity id.
 */
export interface PresetExportOption {
  id?: string;
  kind: string;
  name?: string;
  aliases?: readonly string[];
  deleted?: boolean;
  /** Set on a `RollerType`; a creeper roller (Bodenrolle) is `4`. */
  rollerType?: unknown;
}

/**
 * Fields that hold an option-database reference. Stored as `{ name }` here,
 * because everything inside this app - aliases, renames, the importer -
 * matches by name; a configurator reads them by id, because a name is a label
 * that gets corrected while an id is not.
 */
const OPTION_REFERENCE_KEYS = [
  "partSelection",
  "rollerA",
  "rollerB",
  "areaPaddingType",
  "borderPaddingType",
] as const;

/**
 * The option kinds an Anbauteil of each category picks its article from -
 * what a preset's `partSelection` may name. Mirrors
 * `USER_OPTION_KINDS_BY_CATEGORY` of `@emerge/lt-cases`, which resolves the
 * exported reference within these kinds only: a name like "Keine" exists
 * under several kinds, and resolving it elsewhere would write an id the
 * configurator refuses. Kinds are searched in order. A test in
 * `@emerge/lt-cases` keeps the two tables equal.
 */
export const PART_SELECTION_KINDS_BY_CATEGORY: Readonly<
  Record<string, readonly string[]>
> = {
  HINGE_SET: ["HingeType", "PianoHingeType"],
  LOCK_SET: ["LockType"],
  ROLLER_BOARD: ["RollerType"],
  FEET: ["FootType"],
  DISHES: ["DishType"],
  HANDLES: ["HandleType"],
  TROLLEY_HANDLES: ["TrolleyHandleType"],
  RUNNERS: ["RunnerType"],
  CUTOUTS: [],
};

const ROLLER_KIND = "RollerType";
const PADDING_KIND = "PaddingType";
/** `ROLLER_TYPE.CREEPER` of `@project/manufacturer`: a Bodenrolle. */
export const CREEPER_ROLLER_TYPE = 4;

/** What keeps a preset from being written; the frontend writes the sentence. */
export enum PresetExportProblemCode {
  /** The user options list is not loaded yet. */
  OptionsNotLoaded = "optionsNotLoaded",
  /** `params.presetName`, `params.id`: the preset's id is no uuid. */
  IdNotUuid = "idNotUuid",
  /** `params.presetName`, `params.field`, `params.name`: no option has this name. */
  ReferenceUnknown = "referenceUnknown",
  /** Also `params.category`: the category picks no article from the options. */
  ReferenceCategoryPicksNoArticle = "referenceCategoryPicksNoArticle",
  /** Also `params.kind`, `params.kinds` (comma separated): the name belongs to an option of another kind. */
  ReferenceOtherKind = "referenceOtherKind",
}

export interface PresetExportProblem {
  code: PresetExportProblemCode;
  params: Record<string, string>;
}

export class PresetExportError extends Error {
  constructor(readonly problems: PresetExportProblem[]) {
    super(
      `${problems.length} presets cannot be exported: ${problems.map((problem) => problem.code).join(", ")}`,
    );
    this.name = "PresetExportError";
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const withoutWhitespace = (value: string) => value.replaceAll(/\s/g, "");

/** A reference's name, trimmed; empty when it has none. */
const referenceName = (raw: Record<string, unknown>): string =>
  typeof raw.name === "string" ? raw.name.trim() : "";

/**
 * Whether an option answers to a name: whitespace-insensitive on its own
 * name, or exactly one of its aliases (options are renamed over time and keep
 * the old name as an alias).
 */
export const answersToName = (
  option: Pick<PresetExportOption, "name" | "aliases">,
  name: string,
): boolean =>
  (typeof option.name === "string" &&
    option.name.length > 0 &&
    withoutWhitespace(option.name) === withoutWhitespace(name)) ||
  (option.aliases?.includes(name) ?? false);

const idAndName = (option: PresetExportOption) => ({
  id: option.id,
  name: option.name,
});

/**
 * Looks up the options a preset's `{ name }` references point at, by kind.
 * Deleted options are left out, as they are everywhere the app offers an
 * article.
 */
class PresetReferenceResolver {
  private readonly optionsByKind = new Map<string, PresetExportOption[]>();

  constructor(options: readonly PresetExportOption[]) {
    for (const option of options) {
      if (option.deleted) continue;
      const ofKind = this.optionsByKind.get(option.kind);
      if (ofKind) ofKind.push(option);
      else this.optionsByKind.set(option.kind, [option]);
    }
  }

  private ofKind(kind: string): readonly PresetExportOption[] {
    return this.optionsByKind.get(kind) ?? [];
  }

  /** The article of a `partSelection`, among the kinds of its category. */
  partSelection(raw: unknown, category: string): unknown {
    if (!isRecord(raw)) return raw;
    const name = referenceName(raw);
    if (!name) return raw;
    const kinds = PART_SELECTION_KINDS_BY_CATEGORY[category] ?? [];
    for (const kind of kinds) {
      const match = this.ofKind(kind).find((option) =>
        answersToName(option, name),
      );
      // An id the reference already carries is kept.
      if (match) return { ...idAndName(match), ...raw, name };
    }
    return raw;
  }

  /**
   * Why a `partSelection` name found no article among the kinds of its
   * category, when the reason is more than "not there at all": the category
   * picks no articles, or the name belongs to an article of another kind.
   */
  partSelectionMismatch(
    name: string,
    category: string,
  ): PresetExportProblem | undefined {
    const kinds = PART_SELECTION_KINDS_BY_CATEGORY[category] ?? [];
    if (kinds.length === 0) {
      return {
        code: PresetExportProblemCode.ReferenceCategoryPicksNoArticle,
        params: { category },
      };
    }
    const trimmed = name.trim();
    if (!trimmed) return undefined;
    for (const [kind, ofKind] of this.optionsByKind) {
      if (ofKind.some((option) => answersToName(option, trimmed))) {
        return {
          code: PresetExportProblemCode.ReferenceOtherKind,
          params: { name: trimmed, kind, kinds: kinds.join(",") },
        };
      }
    }
    return undefined;
  }

  /** A creeper roller (Bodenrolle) by name; a roller board is left as is. */
  roller(raw: unknown): unknown {
    if (!isRecord(raw) || raw.board) return raw;
    const name = referenceName(raw);
    if (!name) return raw;
    const match = this.ofKind(ROLLER_KIND).find(
      (option) =>
        option.rollerType === CREEPER_ROLLER_TYPE &&
        typeof option.name === "string" &&
        withoutWhitespace(option.name) === withoutWhitespace(name),
    );
    return match ? idAndName(match) : raw;
  }

  /** A padding by name or alias; `"default"` names the first one. */
  padding(raw: unknown): unknown {
    if (!isRecord(raw) || raw.strength !== undefined) return raw;
    const name = referenceName(raw);
    if (!name) return raw;
    const paddings = this.ofKind(PADDING_KIND);
    const match =
      name === "default"
        ? paddings[0]
        : paddings.find((option) => answersToName(option, name));
    return match ? idAndName(match) : raw;
  }

  /** The preset data with every name reference resolved where it can be. */
  resolve(
    data: Record<string, unknown>,
    category: string,
  ): Record<string, unknown> {
    const result = { ...data };
    if (result.partSelection) {
      result.partSelection = this.partSelection(result.partSelection, category);
    }
    if (result.rollerA) result.rollerA = this.roller(result.rollerA);
    if (result.rollerB) result.rollerB = this.roller(result.rollerB);
    if (result.areaPaddingType) {
      result.areaPaddingType = this.padding(result.areaPaddingType);
    }
    if (result.borderPaddingType) {
      result.borderPaddingType = this.padding(result.borderPaddingType);
    }
    return result;
  }
}

/**
 * Rewrites one preset's option references in place, from the `{ name }` this
 * app stores to the `{ id, name }` a configurator reads.
 *
 * Returns what could not be resolved; an empty list means the preset is
 * ready to be written. Every key is looked at even once one has failed, so
 * the user is told about all of them at once rather than one per attempt.
 */
const resolveOptionReferences = (
  data: Record<string, unknown>,
  preset: ExportablePreset,
  resolver: PresetReferenceResolver,
): PresetExportProblem[] => {
  const problems: PresetExportProblem[] = [];

  for (const key of OPTION_REFERENCE_KEYS) {
    const reference = data[key];
    if (reference === undefined || reference === null) continue;
    if (!isRecord(reference)) continue;

    const referenceId = reference.id;
    if (typeof referenceId !== "string" || referenceId.length === 0) {
      // Only a string name reads as a name; anything else is reported as
      // unknown rather than as "[object Object]".
      const name = typeof reference.name === "string" ? reference.name : "?";
      const mismatch =
        key === "partSelection"
          ? resolver.partSelectionMismatch(name, preset.category)
          : undefined;
      const where = { presetName: preset.name, field: key, name };
      problems.push(
        mismatch
          ? { code: mismatch.code, params: { ...where, ...mismatch.params } }
          : {
              code: PresetExportProblemCode.ReferenceUnknown,
              params: where,
            },
      );
      continue;
    }

    data[key] = { id: referenceId, name: reference.name };
  }

  return problems;
};

/**
 * The presets as a configurator's `presets.json`.
 *
 * The conversion that makes this more than a `JSON.stringify` of the list is
 * the option references: this app stores them by name, and a configurator
 * reads them by id. A preset whose reference no longer resolves, or whose own
 * id is not a uuid, is reported rather than written - the configurator would
 * refuse the file on load, and it is better to say which Vorlage is at fault
 * while the user is still looking at them.
 *
 * `options` is the user options list (`null` while it is not loaded yet).
 * `texts` picks the name that is written: the German `name`, or for a
 * configurator the trimmed `nameEn`. The caller reports an empty English name;
 * it is written as it is.
 */
export function buildPresetExport(
  presets: readonly ExportablePreset[],
  options: readonly PresetExportOption[] | null | undefined,
  texts: PresetExportTexts = "german",
): ExportedPreset[] {
  if (!options) {
    throw new PresetExportError([
      { code: PresetExportProblemCode.OptionsNotLoaded, params: {} },
    ]);
  }

  const resolver = new PresetReferenceResolver(options);
  const problems: PresetExportProblem[] = [];
  const exported: ExportedPreset[] = [];

  for (const preset of presets) {
    if (preset.deleted) continue;

    if (!isPresetId(preset.id)) {
      problems.push({
        code: PresetExportProblemCode.IdNotUuid,
        params: { presetName: preset.name, id: JSON.stringify(preset.id) },
      });
      continue;
    }
    const id = preset.id.toLowerCase();

    const data = isRecord(preset.data)
      ? resolver.resolve(preset.data, preset.category)
      : {};

    // The export needs the id that names an article and the name that reads
    // it, and nothing else - the rest is the consumer's own option database
    // to supply.
    const referenceProblems = resolveOptionReferences(data, preset, resolver);
    if (referenceProblems.length > 0) {
      problems.push(...referenceProblems);
      continue;
    }

    // Fields that name one mounted Anbauteil rather than the template. A preset
    // saved from a case carries them and they must not travel with it.
    const { tags: _tags, ...templateData } = presetDataFromInstance(data);

    exported.push({
      id,
      name: texts === "english" ? (preset.nameEn ?? "").trim() : preset.name,
      productType: preset.productType,
      category: preset.category,
      ...(preset.tags && preset.tags.length > 0 ? { tags: preset.tags } : {}),
      data: templateData,
    });
  }

  if (problems.length > 0) throw new PresetExportError(problems);
  return exported;
}

/** A preset in the internal export, which keeps the flag an import restores. */
export interface InternalExportedPreset extends ExportedPreset {
  availableExternally: boolean;
  nameEn: string;
}

/**
 * The presets as this app's own JSON export ("Als JSON exportieren"): the
 * configurator's `presets.json` plus each preset's `availableExternally` and
 * `nameEn`, so that exporting and importing again keeps the Freigabe and the
 * English name.
 */
export function buildInternalPresetExport(
  presets: readonly ExportablePreset[],
  options: readonly PresetExportOption[] | null | undefined,
): InternalExportedPreset[] {
  const flagById = new Map(
    presets.map((preset) => [
      preset.id.toLowerCase(),
      preset.availableExternally === true,
    ]),
  );
  const nameEnById = new Map(
    presets.map((preset) => [preset.id.toLowerCase(), preset.nameEn ?? ""]),
  );
  return buildPresetExport(presets, options).map((exported) => ({
    ...exported,
    availableExternally: flagById.get(exported.id) ?? false,
    nameEn: nameEnById.get(exported.id) ?? "",
  }));
}
